import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  Check, AlertTriangle, CreditCard, Package, Clock, ShieldCheck, 
  Zap, Crown, Loader2, Sparkles, X, ChevronDown, ChevronUp,
  Plus, Settings, Users, MapPin, HardDrive, Percent, AlertCircle, Info,
  ArrowUpCircle, XCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useCurrency } from '../../context/CurrencyContext';
import DeactivationModal from '../../components/vendor/DeactivationModal';
import UniversalPaymentCheckout from '../../components/UniversalPaymentCheckout';
import CancelAnnualModal from '../../components/vendor/CancelAnnualModal';
import UpgradePreviewModal from '../../components/vendor/UpgradePreviewModal';
import BillingStateModal from '../../components/vendor/BillingStateModal';

// Communication add-ons (full month billing)
const COMMUNICATION_ADDONS = ['sms_marketing', 'email_marketing', 'whatsapp_promotional', 'whatsapp_crm'];

// Feature groups for comparison table - synced with Admin PLAN_FEATURES
const FEATURE_GROUPS = [
  {
    name: 'Pricing & Commission',
    features: [
      { key: 'monthly_price', label: 'Monthly Price', format: 'currency' },
      { key: 'annual_price', label: 'Annual Price', format: 'currency' },
      { key: 'commission_percentage', label: 'Commission Rate', format: 'percentage' },
      { key: 'lead_base_price', label: 'Lead Base Price', format: 'currency' },
    ]
  },
  {
    name: 'Listings & Catalog',
    features: [
      { key: 'max_listings', label: 'No. of Listings', format: 'number' },
      { key: 'listing_types_included', label: 'No. of Listing Types', format: 'number' },
      { key: 'product_addons_enabled', label: 'Variants / Product Options (Add-Ons)', format: 'boolean' },
      { key: 'master_catalogue_access', label: 'Master Catalogues Access', format: 'select' },
    ]
  },
  {
    name: 'Storage & Media',
    features: [
      { key: 'media_storage_mb', label: 'Media Storage (MB)', format: 'storage' },
      { key: 'ad_credits', label: 'Ad Credits', format: 'number' },
    ]
  },
  {
    name: 'Team & Locations',
    features: [
      { key: 'team_members_included', label: 'No. of Team Members', format: 'number' },
      { key: 'max_locations', label: 'No. of Locations', format: 'number' },
    ]
  },
  {
    name: 'Features & Integrations',
    features: [
      { key: 'events_ticketing_enabled', label: 'Events Ticket with QR & Reader App', format: 'boolean' },
      { key: 'gst_billing_enabled', label: 'GST Billing', format: 'boolean' },
      { key: 'zoho_invoice_enabled', label: 'Zoho Invoice', format: 'boolean' },
      { key: 'international_markets_enabled', label: 'International Markets', format: 'boolean' },
      { key: 'reviews_ratings_enabled', label: 'Reviews & Ratings', format: 'boolean' },
    ]
  },
  {
    name: 'Communications',
    features: [
      { key: 'whatsapp_transactional', label: 'WhatsApp Transactional Msgs', format: 'select' },
    ]
  },
  {
    name: 'Support',
    features: [
      { key: 'support_level', label: 'Support Type', format: 'support' },
    ]
  },
];

const SubscriptionHub = () => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const { formatPrice } = useCurrency();
  const [searchParams] = useSearchParams();
  const trialGateReason = searchParams.get('gate'); // 'trial_expired' | 'suspended' | null
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [showFullFeatures, setShowFullFeatures] = useState(false);
  const [featureTab, setFeatureTab] = useState('features');
  
  // Add-ons state
  const [addons, setAddons] = useState([]);
  const [activatingAddon, setActivatingAddon] = useState(null);
  const [addonsLoading, setAddonsLoading] = useState(false);
  
  // Monthly Statement & Overflow state
  const [monthlyStatement, setMonthlyStatement] = useState(null);
  const [overflowStatus, setOverflowStatus] = useState(null);
  const [showStatement, setShowStatement] = useState(false);
  
  // Deactivation state
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  
  // Payment checkout state (Universal Payment Modal)
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);

  // iter-230bw Phase 4 — Cancel-Annual, Upgrade-Preview, Billing-State modals.
  const [showCancelAnnualModal, setShowCancelAnnualModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null); // { fromCode, toCode, toName }
  const [showBillingStateModal, setShowBillingStateModal] = useState(false);
  const [billingStatePendingRetry, setBillingStatePendingRetry] = useState(null);
  
  // Ref for scrolling to plans section
  const plansSectionRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const plansData = await apiCall('/api/subscription-plans/public');
      setPlans(plansData.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      
      try {
        const [subData, usageData] = await Promise.all([
          apiCall('/api/vendor-subscription/my-subscription'),
          apiCall('/api/vendor-subscription/my-usage')
        ]);
        setSubscription(subData.subscription);
        setCurrentPlan(subData.plan);
        setUsage(usageData);
      } catch (error) {
      }
      
      // Fetch add-ons
      await fetchAddons();
      
      // Fetch monthly statement and overflow check
      await fetchStatement();
      await checkOverflow();
      // iter-230bw — Fetch GST invoice list (best-effort).
      fetchGstInvoices();
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  // iter-230bw Phase 3 — GST subscription invoice list + PDF download.
  const [gstInvoices, setGstInvoices] = useState([]);
  const [bulkPeriod, setBulkPeriod] = useState('month'); // month | fy | range
  const [bulkYear, setBulkYear] = useState(String(new Date().getFullYear()));
  const [bulkMonth, setBulkMonth] = useState(String(new Date().getMonth() + 1));
  const [bulkFy, setBulkFy] = useState('');
  const [bulkFrom, setBulkFrom] = useState('');
  const [bulkTo, setBulkTo] = useState('');
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const fetchGstInvoices = async () => {
    try {
      const res = await apiCall('/api/subscription-billing/invoices?limit=20');
      setGstInvoices(res?.invoices || []);
    } catch (_e) { /* silent — list is best-effort */ }
  };

  const bulkDownloadInvoices = async () => {
    const qs = new URLSearchParams({ period: bulkPeriod });
    if (bulkPeriod === 'month') {
      if (!bulkYear || !bulkMonth) {
        toast({ title: 'Pick year and month', variant: 'destructive' });
        return;
      }
      qs.set('year', bulkYear);
      qs.set('month', bulkMonth);
    } else if (bulkPeriod === 'fy') {
      if (!bulkFy) {
        toast({ title: 'Pick a financial year (e.g. FY25-26)', variant: 'destructive' });
        return;
      }
      qs.set('fy', bulkFy);
    } else if (bulkPeriod === 'range') {
      if (!bulkFrom || !bulkTo) {
        toast({ title: 'Pick from + to dates', variant: 'destructive' });
        return;
      }
      qs.set('from', new Date(bulkFrom).toISOString());
      qs.set('to', new Date(bulkTo).toISOString());
    }
    setBulkDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/subscription-billing/invoices/bulk-zip?${qs}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (resp.status === 404) {
        toast({ title: 'No invoices in the selected window.' });
        return;
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        toast({ title: 'Bulk download failed', description: body?.detail || `HTTP ${resp.status}`, variant: 'destructive' });
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `aniyor-invoices-${bulkPeriod}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast({ title: 'Download started', description: `${resp.headers.get('X-Invoice-Count') || ''} invoices` });
    } catch (e) {
      toast({ title: 'Bulk download failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setBulkDownloading(false);
    }
  };

  const downloadGstInvoice = async (invoiceNumber) => {
    try {
      // We can't go through apiCall (it parses JSON). Hit the raw URL with
      // the bearer token so the browser renders the PDF stream.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/subscription-billing/invoices/${encodeURIComponent(invoiceNumber)}/pdf`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (resp.status === 400) {
        const body = await resp.json().catch(() => ({}));
        if ((body?.detail || '').toLowerCase().includes('billing state')
            || body?.code === 'BILLING_STATE_REQUIRED') {
          setBillingStatePendingRetry(() => () => downloadGstInvoice(invoiceNumber));
          setShowBillingStateModal(true);
          return;
        }
        toast({ title: 'Could not download invoice', description: body?.detail || 'Bad request', variant: 'destructive' });
        return;
      }
      if (!resp.ok) {
        toast({ title: 'Could not download invoice', description: `HTTP ${resp.status}`, variant: 'destructive' });
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${invoiceNumber.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast({ title: 'Download failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
  };

  const fetchStatement = async () => {
    try {
      const statement = await apiCall('/api/vendor-subscription/monthly-statement');
      setMonthlyStatement(statement);
    } catch (error) {
    }
  };

  const checkOverflow = async () => {
    try {
      const overflow = await apiCall('/api/vendor-subscription/overflow-check');
      setOverflowStatus(overflow);
    } catch (error) {
    }
  };

  const fetchAddons = async () => {
    try {
      setAddonsLoading(true);
      const addonsData = await apiCall('/api/vendor-subscription/my-addons');
      setAddons(addonsData.addons || []);
    } catch (error) {
    } finally {
      setAddonsLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    // Check if this is a paid plan that requires payment
    const price = billingCycle === 'annual' ? plan.annual_price : plan.monthly_price;

    // iter-230bw Phase 4 — If the vendor has an active monthly subscription
    // and is picking a higher tier on the SAME monthly cycle, route to the
    // pro-rated upgrade flow instead of opening a fresh subscription.
    const TIER_RANK = { launch: 1, basic: 2, professional: 3, business: 4, enterprise: 5 };
    const isActiveMonthly = (
      subscription
      && (subscription.status === 'active' || subscription.subscription_status === 'active')
      && (subscription.subscription_type === 'monthly' || subscription.billing_cycle === 'monthly')
    );
    if (
      isActiveMonthly
      && billingCycle === 'monthly'
      && (TIER_RANK[plan.plan_code?.toLowerCase()] || 0) > (TIER_RANK[subscription?.plan_code?.toLowerCase()] || 0)
    ) {
      setUpgradeContext({
        fromCode: subscription.plan_code,
        toCode: plan.plan_code,
        toName: plan.plan_name,
      });
      return;
    }
    
    if (price > 0 && !plan.is_custom_pricing) {
      // Open Universal Payment Checkout for paid plans
      setSelectedPlanForPayment(plan);
      setShowPaymentCheckout(true);
      return;
    }
    
    // For free plans or custom pricing, use the original flow
    try {
      setSubscribing(true);
      await apiCall('/api/vendor-subscription/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
          apply_trial: !subscription
        })
      });
      toast({
        title: 'Success!',
        description: `Successfully subscribed to ${plan.plan_name}!`
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Subscription Failed',
        description: error.message || 'Failed to subscribe',
        variant: 'destructive'
      });
    } finally {
      setSubscribing(false);
    }
  };

  // Handle successful payment from Universal Checkout
  const handlePaymentSuccess = (result) => {
    toast({
      title: 'Subscription Activated!',
      description: `You are now subscribed to ${selectedPlanForPayment?.plan_name}!`
    });
    setShowPaymentCheckout(false);
    setSelectedPlanForPayment(null);
    fetchData();
  };

  // Handle payment error
  const handlePaymentError = (error) => {
    toast({
      title: 'Payment Failed',
      description: error.description || 'Payment was not successful',
      variant: 'destructive'
    });
  };

  const handleStartTrial = async (plan) => {
    try {
      setSubscribing(true);
      await apiCall('/api/vendor-subscription/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
          apply_trial: true,
          preserve_trial_days: isTrialActive
        })
      });
      toast({
        title: 'Trial Started!',
        description: `You're now on a 30-day trial of ${plan.plan_name}!`
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Trial Activation Failed',
        description: error.message || 'Failed to start trial',
        variant: 'destructive'
      });
    } finally {
      setSubscribing(false);
    }
  };

  const handleActivateAddon = async (addon) => {
    try {
      setActivatingAddon(addon.key);
      const result = await apiCall('/api/vendor-subscription/addons/activate', {
        method: 'POST',
        body: JSON.stringify({ addon_key: addon.key })
      });
      
      toast({
        title: 'Add-on Activated!',
        description: `${addon.name} has been activated. ${result.billing_info?.billing_note || ''}`
      });
      fetchAddons();
      fetchData();
    } catch (error) {
      toast({
        title: 'Activation Failed',
        description: error.message || 'Failed to activate add-on',
        variant: 'destructive'
      });
    } finally {
      setActivatingAddon(null);
    }
  };

  const handleDeactivateAddon = async (addon) => {
    try {
      setActivatingAddon(addon.key);
      const result = await apiCall('/api/vendor-subscription/addons/deactivate', {
        method: 'POST',
        body: JSON.stringify({ addon_key: addon.key })
      });
      
      toast({
        title: 'Add-on Deactivated',
        description: result.deactivation_info?.note || `${addon.name} has been deactivated.`
      });
      fetchAddons();
      fetchData();
    } catch (error) {
      toast({
        title: 'Deactivation Failed',
        description: error.message || 'Failed to deactivate add-on',
        variant: 'destructive'
      });
    } finally {
      setActivatingAddon(null);
    }
  };

  // Handle account deactivation
  const handleAccountDeactivation = async (surveyData) => {
    try {
      setDeactivating(true);
      await apiCall('/api/vendor-subscription/deactivate-account', {
        method: 'POST',
        body: JSON.stringify(surveyData)
      });
      
      toast({
        title: 'Account Deactivated',
        description: 'Your account has been deactivated. You have 60 days to reactivate.',
        variant: 'destructive'
      });
      
      // Refresh subscription status
      fetchData();
    } catch (error) {
      toast({
        title: 'Deactivation Failed',
        description: error.message || 'Failed to deactivate account',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setDeactivating(false);
    }
  };

  // Scroll to plans section
  const scrollToPlans = () => {
    plansSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getPlanColor = (planCode, type = 'bg') => {
    const colors = {
      'basic': { bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-500' },
      'free': { bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-500' },
      'pro': { bg: 'bg-purple-600', gradient: 'from-purple-500 to-purple-700', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-500' },
      'professional': { bg: 'bg-purple-600', gradient: 'from-purple-500 to-purple-700', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-500' },
      'business': { bg: 'bg-amber-500', gradient: 'from-amber-400 to-orange-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-500' },
      'enterprise': { bg: 'bg-rose-500', gradient: 'from-amber-500 via-orange-500 to-rose-500', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-500' }
    };
    const colorSet = colors[planCode?.toLowerCase()] || colors['basic'];
    return colorSet[type] || colorSet.bg;
  };

  const getPlanIcon = (planCode) => {
    const icons = {
      'basic': <Package className="w-6 h-6" />,
      'free': <Package className="w-6 h-6" />,
      'pro': <Zap className="w-6 h-6" />,
      'professional': <Zap className="w-6 h-6" />,
      'business': <Crown className="w-6 h-6" />,
      'enterprise': <Crown className="w-6 h-6" />
    };
    return icons[planCode?.toLowerCase()] || <Package className="w-6 h-6" />;
  };

  const formatValue = (value, format) => {
    switch (format) {
      case 'currency':
        if (value === -1) return 'Custom';
        if (value === 0) return 'Free';
        return formatPrice(value);
      case 'percentage':
        return `${value}%`;
      case 'number':
        if (value === -1) return 'Custom';
        if (value === 0 || value >= 9999) return 'Unlimited';
        return value?.toString();
      case 'storage':
        if (value === -1) return 'Custom';
        if (value >= 1024) return `${(value / 1024).toFixed(0)} GB`;
        return `${value} MB`;
      case 'boolean':
        return value ? (
          <div className="flex justify-center">
            <Check className="w-5 h-5 text-green-600" />
          </div>
        ) : (
          <div className="flex justify-center">
            <X className="w-5 h-5 text-slate-300" />
          </div>
        );
      case 'select':
        // Handle select values like master_catalogue_access, whatsapp_transactional
        const selectDisplayMap = {
          'none': <div className="flex justify-center"><X className="w-5 h-5 text-slate-300" /></div>,
          'included': <div className="flex justify-center"><Check className="w-5 h-5 text-green-600" /></div>,
          'add_on': <Badge variant="outline" className="text-xs">Add-on</Badge>,
          'read_only': <Badge className="bg-blue-100 text-blue-700 text-xs">Read Only</Badge>,
          'full': <div className="flex justify-center"><Check className="w-5 h-5 text-green-600" /></div>,
        };
        return selectDisplayMap[value] || <span className="text-sm">{value || '-'}</span>;
      case 'support':
        // Handle support_level which can be array or string
        if (Array.isArray(value)) {
          return <span className="text-sm capitalize">{value.join(', ')}</span>;
        }
        const supportDisplayMap = {
          'email': 'Email',
          'email_chat': 'Email + Chat',
          'call': 'Call',
          'priority': <Badge className="bg-purple-100 text-purple-700 text-xs">Priority</Badge>,
          'dedicated': <Badge className="bg-amber-100 text-amber-700 text-xs">Dedicated</Badge>
        };
        return supportDisplayMap[value] || <span className="text-sm">{value || '-'}</span>;
      default:
        return value?.toString() || '-';
    }
  };

  // Subscription state — iter-231-I trial-expired state machine fix.
  // Trial is "expired" when ANY of:
  //   • status === 'trial' AND days_remaining <= 0 (within-trial timeout)
  //   • status is 'expired' / 'trial_expired' / 'past_due' (post-trial / lapsed)
  //   • URL gate query param `?gate=trial_expired` (StorefrontGate redirect)
  //   • `trial_used === true` on the subscription doc AND no active payment
  const isTrialActive = subscription?.status === 'trial';
  const trialDaysRemaining = usage?.trial_days_remaining || 0;
  const isPaymentActive = subscription?.status === 'active' && !isTrialActive;
  const subStatus = (subscription?.status || '').toLowerCase();
  const trialUsed = subscription?.trial_used === true || subscription?.trial_started_at != null;
  const isTrialExpired =
    (isTrialActive && trialDaysRemaining <= 0) ||
    ['expired', 'trial_expired', 'past_due', 'lapsed', 'cancelled', 'canceled'].includes(subStatus) ||
    trialGateReason === 'trial_expired' ||
    (trialUsed && !isPaymentActive && !isTrialActive);
  const hasNoSubscription = !subscription;

  // iter-296 — the vendor's REAL billing cycle (never the pricing toggle).
  const subscriptionCycle = subscription?.billing_cycle || subscription?.subscription_type || null;

  // Get active add-ons from subscription
  const activeAddons = subscription?.active_addons || [];
  const totalAddonCost = addons.filter(a => a.is_active).reduce((sum, a) => sum + (a.price || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 md:p-6 max-w-7xl mx-auto">

        {/* iter-221c — Trial-gate banner (only shown when redirected here
            from a locked vendor route via the TrialGate wrapper). */}
        {trialGateReason && (
          <div
            className={`mb-6 border-l-4 p-4 rounded-r-lg ${
              trialGateReason === 'suspended'
                ? 'bg-rose-50 border-rose-500'
                : 'bg-amber-50 border-amber-500'
            }`}
            data-testid={`trial-gate-banner-${trialGateReason}`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={`h-6 w-6 flex-shrink-0 mt-0.5 ${
                  trialGateReason === 'suspended' ? 'text-rose-500' : 'text-amber-500'
                }`}
              />
              <div className="flex-1">
                <h4
                  className={`font-bold ${
                    trialGateReason === 'suspended' ? 'text-rose-900' : 'text-amber-900'
                  }`}
                >
                  {trialGateReason === 'suspended'
                    ? 'Account Suspended'
                    : 'Your trial has ended'}
                </h4>
                <p
                  className={`text-sm ${
                    trialGateReason === 'suspended' ? 'text-rose-700' : 'text-amber-700'
                  }`}
                >
                  {trialGateReason === 'suspended' ? (
                    <>
                      Your account has been planless for over 30 days, so we’ve
                      suspended new orders and unpublished your listings.
                      Pick a plan below — once you subscribe, support will
                      help you re-publish your catalogue. Or email{' '}
                      <a className="underline" href="mailto:support@aniyor.net">
                        support@aniyor.net
                      </a>{' '}
                      for help.
                    </>
                  ) : (
                    <>
                      Your 30-day Professional trial is over. Your storefront
                      is paused — buyers can browse but can’t purchase until
                      you pick a plan below.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overflow Alert (Critical - Red) */}
        {overflowStatus?.is_overflow && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 h-6 w-6 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-bold text-red-900">⚠️ Tier Limit Exceeded</h4>
                <p className="text-red-700 text-sm">
                  You have <strong>{overflowStatus.listings_count}</strong> listings but your limit is <strong>{overflowStatus.total_limit}</strong>.
                  {overflowStatus.overflow_count} listing(s) will be auto-unpublished.
                </p>
                <p className="text-red-600 text-xs mt-1">{overflowStatus.action_required}</p>
              </div>
              <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100">
                Upgrade Plan
              </Button>
            </div>
          </div>
        )}

        {/* Grandfathered Status (Info) */}
        {subscription?.is_grandfathered && (
          <div className="mb-6 bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-purple-500 h-6 w-6" />
              <div>
                <h4 className="font-bold text-purple-900">🛡️ Stability Shield Active</h4>
                <p className="text-purple-700 text-sm">
                  Your plan limits are locked from your activation date. Admin changes won't affect you until your cycle ends.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Alerts */}
        <div className="mb-6">
          {isTrialActive && !isTrialExpired && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg flex items-center justify-between" data-testid="trial-active-banner">
              <div className="flex items-center gap-3">
                <Clock className="text-blue-500 h-6 w-6" />
                <div>
                  <h4 className="font-bold text-blue-900">Trial — ₹0 charged</h4>
                  <p className="text-blue-700 text-sm">
                    You have <strong>{trialDaysRemaining} days</strong> left in your {currentPlan?.plan_name || 'plan'} trial.
                    Billing starts automatically the day after your trial ends.
                  </p>
                </div>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                <CreditCard className="h-4 w-4 mr-2" />
                Activate Billing Now
              </Button>
            </div>
          )}

          {isTrialExpired && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg flex items-center justify-between" data-testid="trial-expired-banner">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-red-500 h-6 w-6" />
                <div>
                  <h4 className="font-bold text-red-900">Subscription Required</h4>
                  <p className="text-red-700 text-sm">Your trial has expired. Choose a plan to reactivate.</p>
                </div>
              </div>
            </div>
          )}

          {/* Removed redundant "Account Active" banner - now shown in Active Plan Card below */}

          {hasNoSubscription && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg flex items-center gap-3">
              <Sparkles className="text-amber-500 h-6 w-6" />
              <div>
                <h4 className="font-bold text-amber-900">Welcome! Choose a Plan to Get Started</h4>
                <p className="text-amber-700 text-sm">Start your 30-day free trial today.</p>
              </div>
            </div>
          )}
        </div>

        {/* Usage Stats (if subscribed) */}
        {usage && subscription && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Your Usage This Month
                {activeAddons.length > 0 && (
                  <Badge className="ml-2 bg-purple-100 text-purple-700">
                    {activeAddons.length} Add-on{activeAddons.length > 1 ? 's' : ''} Active
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Listings</span>
                    <span className="font-medium">{usage.listings_used} / {usage.listings_limit === 0 ? '∞' : usage.listings_limit}</span>
                  </div>
                  <Progress value={usage.listings_limit > 0 ? Math.min(usage.listings_percentage, 100) : 0} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="font-medium">{usage.storage_used_mb} MB / {usage.storage_limit_mb} MB</span>
                  </div>
                  <Progress value={Math.min(usage.storage_percentage, 100)} className="h-2" />
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Commission</div>
                  <div className="text-2xl font-bold">{usage.commission_rate}%</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Renewal</div>
                  <div className="text-lg font-semibold">{usage.days_until_renewal || '-'} days</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground">Add-ons Cost</div>
                  <div className="text-lg font-semibold">{formatPrice(totalAddonCost)}/mo</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Plan Card with Actions */}
        {subscription && currentPlan && (
          <Card className="mb-6 border-2 border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${getPlanColor(currentPlan.plan_code, 'bg')} text-white flex items-center justify-center shadow-md`}>
                    {currentPlan.plan_code === 'enterprise' ? <Crown className="w-6 h-6" /> : 
                     currentPlan.plan_code === 'business' ? <Crown className="w-6 h-6" /> :
                     currentPlan.plan_code === 'professional' ? <Zap className="w-6 h-6" /> :
                     <Package className="w-6 h-6" />}
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {['active', 'trial', 'trialing'].includes(subscription.status) ? 'Active Plan' : 'Current Plan'}: {currentPlan.plan_name}
                      {/* iter-296 — badges reflect the vendor's REAL subscription
                          status + billing cycle (previously they mirrored the
                          pricing toggle, showing "Active / Annual" even on an
                          expired monthly sub). */}
                      <Badge
                        data-testid="active-plan-status-badge"
                        className={
                          subscription.status === 'active' ? 'bg-green-100 text-green-700'
                          : ['trial', 'trialing'].includes(subscription.status) ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                        }
                      >
                        {subscription.status === 'active' ? 'Active'
                          : ['trial', 'trialing'].includes(subscription.status) ? 'Trial'
                          : (subscription.status || 'Unknown').charAt(0).toUpperCase() + (subscription.status || 'Unknown').slice(1)}
                      </Badge>
                      {subscriptionCycle && (
                        <Badge variant="outline" data-testid="active-plan-cycle-badge" className={subscriptionCycle === 'annual' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600'}>
                          {subscriptionCycle === 'annual' ? 'Annual' : 'Monthly'}
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-slate-500">
                      {subscriptionCycle === 'annual' ? 'Billed Annually' : 'Billed Monthly'} • 
                      Renews {usage?.days_until_renewal ? `in ${usage.days_until_renewal} days` : 'on next cycle'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-800">
                    {formatPrice(subscriptionCycle === 'annual' ? currentPlan.annual_price : currentPlan.monthly_price)}
                    <span className="text-sm font-normal text-slate-500">/{subscriptionCycle === 'annual' ? 'yr' : 'mo'}</span>
                  </p>
                  <p className="text-xs text-slate-400">+GST @18%</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 border-t">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-6 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    <span>{currentPlan.max_listings === 0 ? 'Unlimited' : currentPlan.max_listings} Listings</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <HardDrive className="w-4 h-4" />
                    <span>{currentPlan.media_storage_mb >= 1024 ? `${(currentPlan.media_storage_mb / 1024).toFixed(0)} GB` : `${currentPlan.media_storage_mb} MB`} Storage</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Percent className="w-4 h-4" />
                    <span>{currentPlan.commission_percentage}% Commission</span>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  {/* Primary Action - Upgrade */}
                  <Button 
                    onClick={scrollToPlans}
                    className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-md"
                  >
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                  
                  {/* Secondary Action - Deactivate */}
                  <button
                    onClick={() => {
                      // iter-230bw Phase 4 — Annual plans use the pro-rated
                      // refund + GST credit-note flow. Monthly plans keep
                      // the existing deactivation survey.
                      const isAnnual = (
                        subscription?.subscription_type === 'annual'
                        || subscription?.billing_cycle === 'annual'
                      );
                      if (isAnnual) {
                        setShowCancelAnnualModal(true);
                      } else {
                        setShowDeactivationModal(true);
                      }
                    }}
                    className="text-sm text-slate-400 hover:text-red-500 transition-colors underline-offset-2 hover:underline"
                    data-testid="cancel-subscription-link"
                  >
                    Cancel Subscription
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Statement Breakdown */}
        {monthlyStatement && subscription && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Monthly Statement
                  <Badge variant="outline" className="text-xs">
                    {monthlyStatement.tax_type}
                  </Badge>
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowStatement(!showStatement)}
                >
                  {showStatement ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-slate-600">Base Plan ({monthlyStatement.base_plan?.name})</span>
                <span className="font-semibold">{formatPrice((monthlyStatement.base_plan?.base_amount || 0))}</span>
              </div>
              
              {monthlyStatement.addons?.length > 0 && (
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-slate-600">Add-ons ({monthlyStatement.addons.length})</span>
                  <span className="font-semibold">{formatPrice((monthlyStatement.addons_subtotal?.base_amount || 0))}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between py-2 border-b text-slate-500">
                <span>GST @{monthlyStatement.base_plan?.gst_rate || 18}%</span>
                <span>{formatPrice((monthlyStatement.grand_total?.gst_amount || 0))}</span>
              </div>
              
              <div className="flex items-center justify-between py-3 font-bold text-lg">
                <span>Total Monthly</span>
                <span className="text-primary">{formatPrice((monthlyStatement.grand_total?.total || 0))}</span>
              </div>

              {/* Detailed breakdown when expanded */}
              {showStatement && monthlyStatement.addons?.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h5 className="text-sm font-semibold mb-2">Add-ons Breakdown:</h5>
                  {monthlyStatement.addons.map((addon, idx) => (
                    <div key={addon.id || addon.name || `${addon.title || "addon"}-${idx}`} className="flex items-center justify-between py-1 text-sm">
                      <div>
                        <span className="text-slate-600">{addon.name}</span>
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {addon.billing_type === 'full_month' ? 'Monthly Cycle' : 'Pro-rata'}
                        </Badge>
                      </div>
                      <span>{formatPrice((addon.total || 0))}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {monthlyStatement.is_grandfathered && (
                <div className="mt-3 p-2 bg-purple-50 rounded-lg text-xs text-purple-700">
                  🛡️ {monthlyStatement.grandfathered_note}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* iter-230bw Phase 3 — GST Tax Invoices */}
        {gstInvoices && gstInvoices.length > 0 && (
          <Card className="mb-6" data-testid="gst-invoices-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Tax Invoices
                <Badge variant="outline" className="text-xs">GST</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Bulk download bar — month | FY | date range */}
              <div className="flex flex-wrap items-end gap-2 mb-3 p-3 bg-slate-50 rounded-lg border" data-testid="bulk-download-bar">
                <select
                  value={bulkPeriod}
                  onChange={(e) => setBulkPeriod(e.target.value)}
                  className="border rounded-md px-2 py-1.5 text-sm"
                  data-testid="bulk-period-select"
                >
                  <option value="month">Month</option>
                  <option value="fy">Financial Year</option>
                  <option value="range">Date Range</option>
                </select>

                {bulkPeriod === 'month' && (
                  <>
                    <select value={bulkMonth} onChange={(e) => setBulkMonth(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" data-testid="bulk-month-select">
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1)}>
                          {new Date(0, i).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number" min="2024" max="2099" value={bulkYear}
                      onChange={(e) => setBulkYear(e.target.value)}
                      className="border rounded-md px-2 py-1.5 text-sm w-24"
                      data-testid="bulk-year-input"
                    />
                  </>
                )}

                {bulkPeriod === 'fy' && (
                  <input
                    type="text" value={bulkFy} placeholder="FY25-26"
                    onChange={(e) => setBulkFy(e.target.value.toUpperCase())}
                    className="border rounded-md px-2 py-1.5 text-sm w-28"
                    data-testid="bulk-fy-input"
                  />
                )}

                {bulkPeriod === 'range' && (
                  <>
                    <input
                      type="date" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)}
                      className="border rounded-md px-2 py-1.5 text-sm" data-testid="bulk-from-input"
                    />
                    <span className="text-xs text-slate-500">to</span>
                    <input
                      type="date" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}
                      className="border rounded-md px-2 py-1.5 text-sm" data-testid="bulk-to-input"
                    />
                  </>
                )}

                <Button
                  size="sm" variant="outline"
                  onClick={bulkDownloadInvoices}
                  disabled={bulkDownloading}
                  data-testid="bulk-download-btn"
                >
                  {bulkDownloading ? 'Bundling…' : 'Download ZIP'}
                </Button>
              </div>

              <div className="divide-y" data-testid="gst-invoices-list">
                {gstInvoices.map((inv) => (
                  <div key={inv.invoice_number} className="flex items-center justify-between py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium font-mono text-xs truncate">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-500">
                        {inv.period_label} · {inv.subscription_type === 'annual' ? 'Annual' : 'Monthly'}
                        {' · '}{inv.amounts?.tax_type === 'IGST' ? 'IGST' : 'CGST+SGST'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{formatPrice(inv.amounts?.grand_total || 0)}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadGstInvoice(inv.invoice_number)}
                        data-testid={`download-invoice-${inv.invoice_number}`}
                      >
                        Download PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header - Plans Section */}
        <div ref={plansSectionRef} className="text-center mb-8 scroll-mt-6">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-2">Choose Your Plan</h2>
          <p className="text-slate-500">Upgrade or downgrade at any time. All prices exclude GST @18%.</p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="relative flex items-center bg-slate-100 p-1 rounded-xl gap-2">
            {/* Annual Tab First with Save 10% label on top */}
            <div className="relative">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm">
                Save 10%
              </div>
              <button 
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                  billingCycle === 'annual' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Annual
              </button>
            </div>
            <button 
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                billingCycle === 'monthly' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {plans.map((plan) => {
            const isSelected = subscription?.plan_code?.toLowerCase() === plan.plan_code?.toLowerCase();
            const price = billingCycle === 'annual' && plan.annual_price > 0 
              ? Math.round(plan.annual_price / 12) 
              : plan.monthly_price;

            return (
              <div 
                key={plan.id}
                className={`relative rounded-2xl border-2 transition-all ${
                  isSelected 
                    ? `${getPlanColor(plan.plan_code, 'border')} shadow-lg scale-[1.02]` 
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
              >
                {(plan.is_featured || isSelected) && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${
                    isSelected 
                      ? `bg-gradient-to-r ${getPlanColor(plan.plan_code, 'gradient')} text-white`
                      : 'bg-amber-500 text-white'
                  } text-[10px] uppercase tracking-widest font-bold py-1 px-3 rounded-full shadow`}>
                    {isSelected ? (isTrialActive ? 'Current Trial' : 'Active') : 'Popular'}
                  </div>
                )}

                <div className="p-5">
                  <div className="h-20 mb-4">
                    <div className={`w-10 h-10 rounded-xl ${getPlanColor(plan.plan_code)} text-white flex items-center justify-center mb-2`}>
                      {getPlanIcon(plan.plan_code)}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{plan.plan_name}</h3>
                    <p className="text-xs text-slate-500 line-clamp-1">{plan.description}</p>
                  </div>

                  <div className="h-14 mb-4">
                    {plan.is_custom_pricing || price === -1 ? (
                      <span className="text-2xl font-black text-slate-900">Custom</span>
                    ) : price === 0 ? (
                      <span className="text-2xl font-black text-slate-900">Free</span>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-black text-slate-900">{formatPrice(price)}</span>
                        <span className="text-slate-400 text-sm">/mo</span>
                        <span className="text-xs text-slate-400">+GST</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mb-4 min-h-[180px]">
                    <div className="flex items-center gap-2 h-6">
                      <Check className={`w-4 h-4 flex-shrink-0 ${getPlanColor(plan.plan_code, 'text')}`} />
                      <span className="text-sm text-slate-600">
                        {plan.max_listings === -1 ? 'Custom' : plan.max_listings === 0 ? 'Unlimited' : plan.max_listings} listings
                      </span>
                    </div>
                    <div className="flex items-center gap-2 h-6">
                      <Check className={`w-4 h-4 flex-shrink-0 ${getPlanColor(plan.plan_code, 'text')}`} />
                      <span className="text-sm text-slate-600">
                        {plan.media_storage_mb === -1 ? 'Custom' : plan.media_storage_mb >= 1024 ? `${(plan.media_storage_mb / 1024).toFixed(0)} GB` : `${plan.media_storage_mb} MB`} storage
                      </span>
                    </div>
                    <div className="flex items-center gap-2 h-6">
                      <Check className={`w-4 h-4 flex-shrink-0 ${getPlanColor(plan.plan_code, 'text')}`} />
                      <span className="text-sm text-slate-600">{plan.commission_percentage}% commission</span>
                    </div>
                    <div className="flex items-center gap-2 h-6">
                      <Check className={`w-4 h-4 flex-shrink-0 ${getPlanColor(plan.plan_code, 'text')}`} />
                      <span className="text-sm text-slate-600">{plan.team_members_included || 1} team member{(plan.team_members_included || 1) > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 h-6">
                      <Check className={`w-4 h-4 flex-shrink-0 ${getPlanColor(plan.plan_code, 'text')}`} />
                      <span className="text-sm text-slate-600">
                        {plan.listing_types_included === -1 ? 'All' : plan.listing_types_included || 7} listing type{(plan.listing_types_included || 7) > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={() => handleSubscribe(plan)}
                      disabled={subscribing || (isSelected && isPaymentActive)}
                      className={`w-full rounded-xl ${
                        isSelected && isPaymentActive
                          ? 'bg-slate-100 text-slate-400 cursor-default'
                          : `bg-gradient-to-r ${getPlanColor(plan.plan_code, 'gradient')} text-white hover:opacity-90`
                      }`}
                      data-testid={`subscribe-plan-${plan.plan_code}`}
                    >
                      {subscribing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isSelected && isPaymentActive ? (
                        'Active Plan'
                      ) : isSelected && isTrialActive && !isTrialExpired ? (
                        'Current Trial'
                      ) : isSelected && isTrialExpired ? (
                        /* iter-231-I — Expired trial on user's last-active plan → "Renew" */
                        'Renew Subscription'
                      ) : (
                        'Subscribe Now'
                      )}
                    </Button>

                    {/* iter-231-I — Hide the Trial CTA once the vendor has used their trial.
                        A trial is a one-time grant; rendering it again after expiry is
                        misleading and breaks the upgrade flow. */}
                    {!isSelected && price > 0 && !trialUsed && !isPaymentActive && (
                      <Button
                        variant="outline"
                        onClick={() => handleStartTrial(plan)}
                        disabled={subscribing}
                        className={`w-full rounded-xl text-sm ${getPlanColor(plan.plan_code, 'text')} border-current`}
                        data-testid={`trial-plan-${plan.plan_code}`}
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        {isTrialActive ? `Try Plan (${trialDaysRemaining} days left)` : 'Try for 30 Days'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Features Button */}
        <div className="text-center mb-6">
          <Button
            variant="outline"
            onClick={() => setShowFullFeatures(!showFullFeatures)}
            className="gap-2"
          >
            {showFullFeatures ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showFullFeatures ? 'Hide Full Features' : 'View All Features'}
          </Button>
        </div>

        {/* Full Feature Comparison Table */}
        {showFullFeatures && (
          <Card className="mb-8">
            <CardContent className="p-0">
              <Tabs value={featureTab} onValueChange={setFeatureTab}>
                <div className="border-b px-4">
                  <TabsList className="bg-transparent">
                    <TabsTrigger value="features" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      All Features
                    </TabsTrigger>
                    <TabsTrigger value="addons" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      Add-ons Pricing
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="features" className="m-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left p-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[200px]">Feature</th>
                          {plans.map(plan => (
                            <th key={plan.id} className="text-center p-3 min-w-[140px]">
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getPlanColor(plan.plan_code, 'light')}`}>
                                {getPlanIcon(plan.plan_code)}
                                <span className="font-semibold">{plan.plan_name}</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {FEATURE_GROUPS.map((group, gIdx) => (
                          <React.Fragment key={gIdx}>
                            <tr className="bg-slate-100">
                              <td colSpan={plans.length + 1} className="p-2 font-semibold text-slate-800 text-sm">
                                {group.name}
                              </td>
                            </tr>
                            {group.features.map((feature, fIdx) => (
                              <tr key={`${gIdx}-${fIdx}`} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="p-3 text-sm text-slate-600 sticky left-0 bg-white">{feature.label}</td>
                                {plans.map(plan => (
                                  <td key={plan.id} className="p-3 text-sm">
                                    <div className="flex items-center justify-center">
                                      {formatValue(plan[feature.key], feature.format)}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="addons" className="m-0 p-4">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {addons.map((addon) => {
                      const isCommunication = COMMUNICATION_ADDONS.includes(addon.key);
                      return (
                        <div key={addon.key} className={`p-4 border rounded-xl transition-all ${
                          addon.is_active ? 'border-purple-300 bg-purple-50' : 'hover:border-purple-300 hover:bg-purple-50'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-slate-900">{addon.name}</h4>
                              <p className="text-xs text-slate-500">
                                {addon.frequency === '/mo' ? 'Per Month' : addon.frequency === 'one-time' ? 'One-time' : addon.frequency}
                              </p>
                              {isCommunication && (
                                <Badge variant="outline" className="text-[10px] mt-1 text-amber-600 border-amber-300">
                                  Full month billing
                                </Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-slate-900">{formatPrice((addon.price || 0))}</div>
                              <div className="text-xs text-slate-400">+GST @{addon.gst_rate || 18}%</div>
                            </div>
                          </div>
                          
                          {addon.is_active ? (
                            <div className="mt-3 space-y-2">
                              <div className="text-xs text-purple-600">
                                ✓ Active since {new Date(addon.activation_date).toLocaleDateString()}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeactivateAddon(addon)}
                                disabled={activatingAddon === addon.key}
                                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                              >
                                {activatingAddon === addon.key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Deactivate'}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleActivateAddon(addon)}
                              disabled={activatingAddon === addon.key || !subscription}
                              className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
                            >
                              {activatingAddon === addon.key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Activate'}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Available Add-ons Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Available Add-ons
              {activeAddons.length > 0 && (
                <Badge className="ml-2 bg-purple-100 text-purple-700">
                  {activeAddons.length} Active
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {addons.slice(0, 8).map((addon) => {
                const isCommunication = COMMUNICATION_ADDONS.includes(addon.key);
                return (
                  <div key={addon.key} className={`flex items-center justify-between p-3 border rounded-lg transition-all ${
                    addon.is_active ? 'border-purple-300 bg-purple-50' : 'hover:border-purple-300'
                  }`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-slate-900 truncate">{addon.name}</h4>
                      <p className="text-xs text-slate-500">
                        {formatPrice((addon.price || 0))} +GST
                      </p>
                    </div>
                    <Switch
                      checked={addon.is_active}
                      disabled={activatingAddon === addon.key || !subscription}
                      onCheckedChange={(checked) => {
                        if (checked) handleActivateAddon(addon);
                        else handleDeactivateAddon(addon);
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-center">
              <Button variant="link" onClick={() => { setShowFullFeatures(true); setFeatureTab('addons'); }}>
                View all add-ons →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimers */}
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-600">
              <Info className="h-4 w-4" />
              Important Information
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-slate-500 space-y-2">
            <p><strong>Billing:</strong> All prices are exclusive of GST @18% as applicable for Indian vendors. International vendors may be eligible for zero-rated GST (export of services).</p>
            <p><strong>Pro-rata Billing:</strong> Regular add-ons are billed on a pro-rata basis based on days remaining in your billing cycle.</p>
            <p><strong>Communication Add-ons:</strong> WhatsApp, SMS, and Email Marketing add-ons follow a strict monthly cycle from the activation date. Full month billing applies regardless of deactivation date within the cycle.</p>
            <p><strong>Leads (LAAS):</strong> Lead purchases are subject to the same GST rules as subscriptions. Lead base price varies by category.</p>
            <p><strong>Trial Period:</strong> Add-ons are NOT free during the trial period. Standard pricing applies.</p>
            <p><strong>Deactivation:</strong> You can deactivate add-ons at any time. Regular add-ons stop immediately; communication add-ons remain active until the end of their billing cycle.</p>
            <p><strong>Tier Limits:</strong> Your effective limits include both your base plan allowances and any active add-ons (e.g., extra team members, extra storage).</p>
          </CardContent>
        </Card>

      </div>
      
      {/* Deactivation Modal */}
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        currentPlan={currentPlan}
        subscription={subscription}
        onDeactivate={handleAccountDeactivation}
        loading={deactivating}
      />
      
      {/* Universal Payment Checkout Modal */}
      <UniversalPaymentCheckout
        isOpen={showPaymentCheckout}
        onClose={() => {
          setShowPaymentCheckout(false);
          setSelectedPlanForPayment(null);
        }}
        userContext={{
          name: user?.name,
          email: user?.email,
          mobile: user?.mobile || user?.phone,
          gstin: user?.gstin || user?.vendor_profile?.gstin,
          razorpayCustomerId: user?.razorpay_customer_id
        }}
        paymentContext={{
          type: 'SUBSCRIPTION',
          amount: billingCycle === 'annual' 
            ? selectedPlanForPayment?.annual_price 
            : selectedPlanForPayment?.monthly_price,
          planId: selectedPlanForPayment?.id,
          metaData: {
            billingCycle: billingCycle,
            planCode: selectedPlanForPayment?.plan_code,
            planName: selectedPlanForPayment?.plan_name
          }
        }}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
      />

      {/* iter-230bw Phase 4 — Annual cancel with pro-rated refund + GST credit note. */}
      <CancelAnnualModal
        isOpen={showCancelAnnualModal}
        onClose={() => setShowCancelAnnualModal(false)}
        subscription={subscription}
        onCancelled={() => {
          setShowCancelAnnualModal(false);
          fetchData();
        }}
      />

      {/* iter-230bw Phase 4 — Pro-rated diff preview + Razorpay checkout for immediate monthly upgrades. */}
      <UpgradePreviewModal
        isOpen={!!upgradeContext}
        onClose={() => setUpgradeContext(null)}
        fromPlanCode={upgradeContext?.fromCode}
        toPlanCode={upgradeContext?.toCode}
        toPlanName={upgradeContext?.toName}
        onSuccess={() => {
          setUpgradeContext(null);
          fetchData();
        }}
      />

      {/* iter-230bw Phase 3 — Option-C billing-state modal hook. */}
      <BillingStateModal
        isOpen={showBillingStateModal}
        onClose={() => {
          setShowBillingStateModal(false);
          setBillingStatePendingRetry(null);
        }}
        onSaved={() => {
          setShowBillingStateModal(false);
          // Retry whatever triggered the modal.
          if (typeof billingStatePendingRetry === 'function') billingStatePendingRetry();
          setBillingStatePendingRetry(null);
        }}
      />
    </DashboardLayout>
  );
};

export default SubscriptionHub;
